import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { useUser, UserRole } from '../contexts/UserContext';
import { apiService } from '../services/apiService';
import Stamp from './layout/Stamp';

type ApplicantType = 'student' | 'parent';
type GridResult = 'Admitted' | 'Rejected' | 'Waitlisted';

interface GridEntry {
  university: string;
  result: GridResult;
  childName?: string;
}

interface ProofDoc {
  url: string;
  fileName: string;
}

// One activity/credential the applicant claims, with up to 3 proof documents.
interface ActivityEntry {
  id: string;
  activity: string; // activity name, e.g. "FRC", "USACO"
  category: string; // activity category, e.g. "robotics"
  tier: string; // state | top300 | championship
  headline: string; // e.g. "Einstein finalist"
  year: string;
  teamNumber: string; // robotics only
  docs: ProofDoc[];
}

const MAX_DOCS_PER_ACTIVITY = 3;

// Rate is set automatically by tier — enforced server-side, shown here for transparency.
const TIER_RATES: Record<string, number> = { state: 85, top300: 110, championship: 150 };

const DEFAULT_TEMPLATE = `1. Context: school, team role, target list (10 min)
2. How I framed the activity on my own application (15 min)
3. Line-by-line activity-list review (25 min)
4. Written next steps, agreed together (10 min)`;

const DRAFT_KEY = 'becomeMentorDraft';

const loadDraft = (): Record<string, any> => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const BecomeMentor: React.FC = () => {
  const navigate = useNavigate();
  const { user, token, login } = useUser();
  const [draft] = useState<Record<string, any>>(loadDraft);
  const [step, setStep] = useState<number>(Math.min(draft.step || 1, 4));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);

  const invalidClass = (field: string) => (invalidFields.includes(field) ? 'invalid' : '');
  const clearInvalid = (field: string) =>
    setInvalidFields((prev) => (prev.includes(field) ? prev.filter((f) => f !== field) : prev));

  // Step 1 — role & basics
  const [applicantType, setApplicantType] = useState<ApplicantType>(
    draft.applicantType === 'parent' ? 'parent' : 'student'
  );
  const [name, setName] = useState(draft.name || user?.name || '');
  const [university, setUniversity] = useState(draft.university || '');
  const [major, setMajor] = useState(draft.major || '');
  const [graduationYear, setGraduationYear] = useState(draft.graduationYear || '2027');
  const [occupation, setOccupation] = useState(draft.occupation || '');
  const [almaMater, setAlmaMater] = useState(draft.almaMater || '');

  // Step 2 — activities & proof
  const [activities, setActivities] = useState<
    Array<{ id: string; name: string; category: string }>
  >([]);
  const [entries, setEntries] = useState<ActivityEntry[]>(
    Array.isArray(draft.entries) ? draft.entries : []
  );
  // Add-activity form
  const [formActivity, setFormActivity] = useState('');
  const [formTier, setFormTier] = useState('state');
  const [formHeadline, setFormHeadline] = useState('');
  const [formYear, setFormYear] = useState('');
  const [formTeam, setFormTeam] = useState('');
  const [formDocs, setFormDocs] = useState<ProofDoc[]>([]);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);

  // Step 3 — application grid
  const [grid, setGrid] = useState<GridEntry[]>(Array.isArray(draft.grid) ? draft.grid : []);
  const [gridSchool, setGridSchool] = useState('');
  const [gridResult, setGridResult] = useState<GridResult>('Admitted');
  const [gridChild, setGridChild] = useState(draft.gridChild || '');

  // Step 4 — sessions (rate is computed, not chosen)
  const [template, setTemplate] = useState(draft.template || DEFAULT_TEMPLATE);

  // Rate follows the highest tier among added activities.
  const computedRate = entries.reduce(
    (max, en) => Math.max(max, TIER_RATES[en.tier] || 0),
    0
  ) || TIER_RATES.state;

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  useEffect(() => {
    apiService.getActivities().then((result) => {
      setActivities(result.activities || []);
      if (result.activities?.length) {
        setFormActivity((prev: string) => prev || result.activities[0].name);
      }
    });
  }, []);

  // Persist the draft so mentors can save & resume anytime.
  useEffect(() => {
    const data = {
      step,
      applicantType,
      name,
      university,
      major,
      graduationYear,
      occupation,
      almaMater,
      entries,
      grid,
      gridChild,
      template,
    };
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(data));
    } catch {
      // Storage full/unavailable — draft persistence is best-effort
    }
  }, [
    step,
    applicantType,
    name,
    university,
    major,
    graduationYear,
    occupation,
    almaMater,
    entries,
    grid,
    gridChild,
    template,
  ]);

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/google`,
        { credential: credentialResponse.credential },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.data.success) {
        let userData = response.data.user;
        // Ensure the mentor role is on the account
        try {
          const addRole = await axios.post(
            `${API_URL}/auth/roles/add`,
            { role: 'mentor' },
            { headers: { Authorization: `Bearer ${response.data.token}` } }
          );
          if (addRole.data.success) userData = addRole.data.user;
        } catch (e) {
          console.error('Error adding mentor role:', e);
        }
        const userRoles: UserRole[] =
          userData.roles && userData.roles.length > 0 ? userData.roles : ['mentor'];
        login(response.data.token, {
          ...userData,
          userType: userData.userType || userRoles.join(','),
          roles: userRoles,
        });
        if (!name) setName(userData.name || '');
      }
    } catch (e) {
      console.error('Google sign-in error:', e);
      alert('Failed to sign in with Google. Please try again.');
    }
  };

  const selectedFormCategory =
    activities.find((a) => a.name === formActivity)?.category || '';
  const formIsRobotics = selectedFormCategory.toLowerCase() === 'robotics';

  const addEntry = () => {
    const fields: string[] = [];
    if (!formActivity) fields.push('entryActivity');
    if (!formHeadline.trim()) fields.push('entryHeadline');
    if (fields.length > 0) {
      setError('Please fill in the highlighted fields to add the activity.');
      setInvalidFields(fields);
      return;
    }
    setError(null);
    setInvalidFields([]);
    setEntries((prev) => [
      ...prev,
      {
        id: `entry-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        activity: formActivity,
        category: selectedFormCategory,
        tier: formTier,
        headline: formHeadline.trim(),
        year: formYear.trim(),
        teamNumber: formIsRobotics ? formTeam.trim() : '',
        docs: formDocs,
      },
    ]);
    // Reset to a fresh, empty form for the next activity
    setFormActivity(activities[0]?.name || '');
    setFormTier('state');
    setFormHeadline('');
    setFormYear('');
    setFormTeam('');
    setFormDocs([]);
  };

  const removeEntry = (id: string) => {
    setEntries((prev) => prev.filter((en) => en.id !== id));
  };

  // Uploads a file and returns its stored URL, or null on failure (sets error).
  const uploadDoc = async (uploadKey: string, file: File): Promise<ProofDoc | null> => {
    if (!token) return null;
    setUploadingKey(uploadKey);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('document', file);
      const res = await axios.post(`${API_URL}/mentors/upload-credential`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success && res.data.url) {
        return { url: res.data.url, fileName: file.name };
      }
      setError(res.data.error || 'Failed to upload proof document. Please try again.');
      return null;
    } catch (e: any) {
      console.error('Error uploading proof document:', e);
      setError(e.response?.data?.error || 'Failed to upload proof document. Please try again.');
      return null;
    } finally {
      setUploadingKey(null);
    }
  };

  // Upload into the add-activity form (before the activity is added)
  const handleFormProofUpload = async (file: File | undefined) => {
    if (!file) return;
    const doc = await uploadDoc('form', file);
    if (doc) {
      setFormDocs((prev) =>
        prev.length < MAX_DOCS_PER_ACTIVITY ? [...prev, doc] : prev
      );
    }
  };

  // Upload onto an already-added activity
  const handleProofUpload = async (entryId: string, file: File | undefined) => {
    if (!file) return;
    const doc = await uploadDoc(entryId, file);
    if (doc) {
      setEntries((prev) =>
        prev.map((en) =>
          en.id === entryId && en.docs.length < MAX_DOCS_PER_ACTIVITY
            ? { ...en, docs: [...en.docs, doc] }
            : en
        )
      );
    }
  };

  const removeProof = (entryId: string, docIndex: number) => {
    setEntries((prev) =>
      prev.map((en) =>
        en.id === entryId ? { ...en, docs: en.docs.filter((_, i) => i !== docIndex) } : en
      )
    );
  };

  const addGridEntry = () => {
    if (!gridSchool.trim()) return;
    setGrid((prev) => [
      ...prev,
      {
        university: gridSchool.trim(),
        result: gridResult,
        childName: applicantType === 'parent' ? gridChild.trim() || undefined : undefined,
      },
    ]);
    setGridSchool('');
    setGridResult('Admitted');
  };

  const removeGridEntry = (index: number) => {
    setGrid((prev) => prev.filter((_, i) => i !== index));
  };

  const validateStep = (n: number): { message: string; fields: string[] } | null => {
    if (n === 1) {
      const fields: string[] = [];
      if (!name.trim()) fields.push('name');
      if (applicantType === 'student') {
        if (!university.trim()) fields.push('university');
        if (!major.trim()) fields.push('major');
        if (!graduationYear.trim()) fields.push('graduationYear');
      } else if (!occupation.trim()) {
        fields.push('occupation');
      }
      if (fields.length > 0)
        return { message: 'Please fill in the highlighted fields.', fields };
    }
    if (n === 2 && applicantType === 'student' && entries.length === 0)
      return {
        message: 'Add at least one activity — it becomes the credential on your profile.',
        fields: ['entryHeadline'],
      };
    if (n === 3) {
      if (applicantType === 'parent' && grid.filter((g) => g.result === 'Admitted').length === 0)
        return {
          message: 'Parent mentors must list at least one admitted school.',
          fields: ['gridSchool'],
        };
      if (applicantType === 'student' && grid.length === 0)
        return {
          message: 'The full application grid is required for listing — add at least one school.',
          fields: ['gridSchool'],
        };
    }
    if (n === 4 && !template.trim())
      return { message: 'A session template is required.', fields: ['template'] };
    return null;
  };

  const goStep = (n: number) => {
    if (n > step) {
      const invalid = validateStep(step);
      if (invalid) {
        setError(invalid.message);
        setInvalidFields(invalid.fields);
        return;
      }
    }
    setError(null);
    setInvalidFields([]);
    setStep(n);
  };

  const handleSubmit = async () => {
    const invalid = validateStep(4);
    if (invalid) {
      setError(invalid.message);
      setInvalidFields(invalid.fields);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setInvalidFields([]);

    try {
      const achievements = entries.map((en, i) => ({
        category: en.activity,
        title: en.teamNumber ? `${en.headline} — Team ${en.teamNumber}` : en.headline,
        icon: en.category.toLowerCase() === 'robotics' ? '🤖' : '🏆',
        year: en.year || undefined,
        isPrimary: i === 0,
        activityTier: en.tier,
        details: en.teamNumber ? { teamNumber: en.teamNumber } : undefined,
        childName: applicantType === 'parent' ? gridChild.trim() || undefined : undefined,
        documents: en.docs,
      }));

      const admissions = grid.map((g) => ({
        university: g.university,
        status: g.result.toLowerCase(), // admitted | rejected | waitlisted
        isAttending:
          applicantType === 'student' &&
          g.result === 'Admitted' &&
          g.university.toLowerCase() === university.trim().toLowerCase(),
        childName: g.childName,
      }));

      const specialties = achievements.slice(0, 5).map((a) => ({
        title: a.title,
        category: a.category,
      }));

      // Note: no pricePerSession — the server sets it from your verified tier.
      const profileData: any = {
        displayName: name.trim(),
        bio: template.trim(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        achievements,
        specialties,
        admissions,
      };

      if (applicantType === 'parent') {
        profileData.mentorType = 'parent';
        profileData.occupation = occupation.trim();
        if (almaMater.trim()) profileData.almaMater = almaMater.trim();
      } else {
        profileData.university = university.trim();
        profileData.major = major.trim();
        profileData.classYear = `Class of ${graduationYear.trim()}`;
        profileData.graduationYear = parseInt(graduationYear.trim(), 10);
      }

      await axios.post(`${API_URL}/mentors/profile`, profileData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      localStorage.removeItem(DRAFT_KEY);
      navigate('/mentor/dashboard', { state: { applicationSubmitted: true } });
    } catch (e: any) {
      console.error('Error submitting application:', e);
      setError(e.response?.data?.error || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const wizClass = (n: number) => (n === step ? 'on' : n < step ? 'done' : '');

  const gridChipClass = (r: GridResult) =>
    r === 'Rejected' ? 'grid-chip reject' : r === 'Waitlisted' ? 'grid-chip waitlist' : 'grid-chip admit';

  const TIER_LABELS: Record<string, string> = {
    state: 'State ranking',
    top300: 'National top-300',
    championship: 'National/international championship',
  };

  const entryMeta = (en: ActivityEntry) =>
    [
      en.activity,
      TIER_LABELS[en.tier] || en.tier,
      en.year || null,
      en.teamNumber ? `Team ${en.teamNumber}` : null,
    ]
      .filter(Boolean)
      .join(' · ');

  return (
    <>
      {/* Hero */}
      <header className="section">
        <div
          className="wrap"
          style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr',
            gap: 64,
            alignItems: 'center',
          }}
        >
          <div>
            <span className="eyebrow" style={{ display: 'block', marginBottom: 16 }}>
              For current college students &amp; veteran parents
            </span>
            <h1>You just made it through. Be the mentor you never had.</h1>
            <p style={{ color: 'var(--ink-secondary)', margin: '20px 0 28px', maxWidth: '52ch' }}>
              You spent years in FRC, VEX, or USACO — and got in. Families one step behind you
              need an hour of exactly what you know. Upload proof of your record, we verify it,
              and we handle clients, payments, and scheduling. Mentors earn $85–150 an hour, set
              automatically by their verified tier.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <a className="btn btn-primary" href="#apply">
                Start your application
              </a>
              <Link className="btn btn-secondary" to="/mentor/dashboard">
                See the mentor dashboard
              </Link>
            </div>
          </div>
          <div>
            <div className="stat-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div className="card">
                <div className="stat">
                  <div className="num plain">$85–150</div>
                  <div className="lbl">Hourly rate — set automatically by your verified tier</div>
                </div>
              </div>
              <div className="card">
                <div className="stat">
                  <div className="num plain">2–4</div>
                  <div className="lbl">Weeks to first booking — supply is capped to demand</div>
                </div>
              </div>
              <div className="card">
                <div className="stat">
                  <div className="num plain">1 hr</div>
                  <div className="lbl">Sessions, on your calendar, from your dorm</div>
                </div>
              </div>
              <div className="card">
                <div className="stat">
                  <div className="num">24–48 hr</div>
                  <div className="lbl">Credential review — our team checks every proof document</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* How it works */}
      <section className="section warm" style={{ padding: '56px 0' }}>
        <div className="wrap row3">
          <div>
            <h3>1. Sign in &amp; build your profile</h3>
            <p className="meta" style={{ fontSize: 14, marginTop: 8 }}>
              Sign in with Google, then add your record and the full application grid — every
              school: admitted, rejected, waitlisted. Your draft saves as you go.
            </p>
          </div>
          <div>
            <h3>2. Upload proof of your record</h3>
            <p className="meta" style={{ fontSize: 14, marginTop: 8 }}>
              Award certificates, results-page screenshots, roster listings. Anything without
              proof is labeled self-reported, never hidden.
            </p>
          </div>
          <div>
            <h3>3. Get verified &amp; go live</h3>
            <p className="meta" style={{ fontSize: 14, marginTop: 8 }}>
              Our team reviews your documents within 24–48 hours. Once approved you're live —
              at the rate your verified tier sets. One ground rule: never promise outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* Application: sign-in gate, then wizard */}
      <section className="section" id="apply">
        <div className="wrap" style={{ maxWidth: 980 }}>
          {!user ? (
            <div className="card" style={{ maxWidth: 560, margin: '0 auto', padding: 36 }}>
              <span className="eyebrow" style={{ display: 'block', marginBottom: 10 }}>
                Mentor application
              </span>
              <h2 style={{ marginBottom: 10 }}>Sign in to start your application</h2>
              <p className="meta" style={{ marginBottom: 20 }}>
                Your application saves to your account as you go — sign in first so nothing is
                lost, and resume anytime from any device.
              </p>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => alert('Google sign-in failed. Please try again.')}
                size="large"
                theme="outline"
                text="continue_with"
                shape="rectangular"
                width="320"
              />
              <ul className="meta" style={{ marginTop: 20, paddingLeft: 18 }}>
                <li>Four short steps — save &amp; resume anytime</li>
                <li>Upload proof once; we verify and set your rate by tier</li>
                <li>Track your application and credential status on your dashboard</li>
              </ul>
            </div>
          ) : (
            <>
              <span className="eyebrow" style={{ display: 'block', marginBottom: 12 }}>
                Mentor application · save &amp; resume anytime
              </span>
              <h2>Build your verified profile</h2>
              <p style={{ color: 'var(--ink-secondary)', marginTop: 8, maxWidth: '60ch' }}>
                Four short steps. The more you prove and disclose, the more you book.
              </p>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '240px 1fr',
                  gap: 40,
                  alignItems: 'start',
                  marginTop: 32,
                }}
              >
                <div className="wiz-nav" role="tablist" aria-label="Application steps">
                  <button className={wizClass(1)} onClick={() => goStep(1)}>
                    1. Role &amp; basics{step > 1 ? ' ✓' : ''}
                  </button>
                  <button className={wizClass(2)} onClick={() => goStep(2)}>
                    2. Activities &amp; proof{step > 2 ? ' ✓' : ''}
                  </button>
                  <button className={wizClass(3)} onClick={() => goStep(3)}>
                    3. Application grid{step > 3 ? ' ✓' : ''}
                  </button>
                  <button className={wizClass(4)} onClick={() => goStep(4)}>
                    4. Sessions &amp; rate
                  </button>
                </div>

                <div>
                  {error && (
                    <div
                      className="note"
                      style={{ marginBottom: 16, color: 'var(--cardinal)' }}
                    >
                      {error}
                    </div>
                  )}

                  {/* 1 — Role & basics */}
                  {step === 1 && (
                    <div className="card">
                      <h3 style={{ marginBottom: 16 }}>Role &amp; basics</h3>
                      <div className="field">
                        <label>I'm applying as</label>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button
                            className={`chip${applicantType === 'student' ? ' on' : ''}`}
                            onClick={() => setApplicantType('student')}
                          >
                            Student mentor — verified admit
                          </button>
                          <button
                            className={`chip${applicantType === 'parent' ? ' on' : ''}`}
                            onClick={() => setApplicantType('parent')}
                          >
                            Parent mentor — veteran family
                          </button>
                        </div>
                        <p className="help">
                          Parent mentors skip the proof upload and complete a cost-and-hours
                          record instead.
                        </p>
                      </div>
                      <div className="row2">
                        <div className="field">
                          <label>Name</label>
                          <input
                            className={invalidClass('name')}
                            value={name}
                            onChange={(e) => {
                              setName(e.target.value);
                              clearInvalid('name');
                            }}
                          />
                        </div>
                        {applicantType === 'student' ? (
                          <div className="field">
                            <label>College</label>
                            <input
                              className={invalidClass('university')}
                              placeholder="e.g. MIT"
                              value={university}
                              onChange={(e) => {
                                setUniversity(e.target.value);
                                clearInvalid('university');
                              }}
                            />
                          </div>
                        ) : (
                          <div className="field">
                            <label>Occupation</label>
                            <input
                              className={invalidClass('occupation')}
                              placeholder="e.g. Software engineer"
                              value={occupation}
                              onChange={(e) => {
                                setOccupation(e.target.value);
                                clearInvalid('occupation');
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <div className="row2">
                        {applicantType === 'student' ? (
                          <>
                            <div className="field">
                              <label>Major</label>
                              <input
                                className={invalidClass('major')}
                                placeholder="e.g. Mechanical Engineering"
                                value={major}
                                onChange={(e) => {
                                  setMajor(e.target.value);
                                  clearInvalid('major');
                                }}
                              />
                            </div>
                            <div className="field">
                              <label>Graduation year</label>
                              <input
                                className={invalidClass('graduationYear')}
                                placeholder="e.g. 2027"
                                value={graduationYear}
                                onChange={(e) => {
                                  setGraduationYear(e.target.value);
                                  clearInvalid('graduationYear');
                                }}
                              />
                            </div>
                          </>
                        ) : (
                          <div className="field">
                            <label>Alma mater (optional)</label>
                            <input
                              placeholder="e.g. UW"
                              value={almaMater}
                              onChange={(e) => setAlmaMater(e.target.value)}
                            />
                          </div>
                        )}
                      </div>
                      <button className="btn btn-primary" onClick={() => goStep(2)}>
                        Continue
                      </button>
                    </div>
                  )}

                  {/* 2 — Activities & proof */}
                  {step === 2 && (
                    <div className="card">
                      <h3 style={{ marginBottom: 4 }}>Activities &amp; proof</h3>
                      <p className="meta" style={{ marginBottom: 20 }}>
                        Add each activity you can mentor in — competition, level reached, and
                        headline achievement — and attach up to {MAX_DOCS_PER_ACTIVITY} proof
                        documents per activity: award certificate, results-page screenshot, or
                        roster listing. Images or PDF, up to 10MB. Anything without proof is
                        labeled self-reported, never hidden.
                      </p>

                      {applicantType === 'parent' && (
                        <div className="note" style={{ marginBottom: 20 }}>
                          Parent mentors: add your kids' activities if you want them shown —
                          proof is optional; your cost-and-hours record is reviewed by our team
                          instead.
                        </div>
                      )}

                      {/* Added activities */}
                      {entries.map((en) => (
                        <div
                          key={en.id}
                          style={{
                            border: '1px solid var(--hairline-strong)',
                            borderRadius: 10,
                            padding: 16,
                            marginBottom: 14,
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              gap: 12,
                              flexWrap: 'wrap',
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 600 }}>{en.headline}</div>
                              <div className="meta">{entryMeta(en)}</div>
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              {en.docs.length > 0 ? (
                                <Stamp variant="verified">
                                  {en.docs.length} proof{en.docs.length !== 1 ? 's' : ''} attached
                                </Stamp>
                              ) : (
                                <Stamp variant="selfrep">Self-reported</Stamp>
                              )}
                              <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => removeEntry(en.id)}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                          <div style={{ marginTop: 10 }}>
                            {en.docs.map((d, di) => (
                              <div
                                key={`${d.url}-${di}`}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  marginBottom: 6,
                                }}
                              >
                                <span className="meta">📄 {d.fileName}</span>
                                <button
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => removeProof(en.id, di)}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                            {uploadingKey === en.id ? (
                              <span className="meta">Uploading…</span>
                            ) : (
                              en.docs.length < MAX_DOCS_PER_ACTIVITY && (
                                <label
                                  className="btn btn-secondary btn-sm"
                                  style={{ cursor: 'pointer' }}
                                >
                                  {en.docs.length === 0
                                    ? 'Upload proof'
                                    : `Add another proof (${en.docs.length}/${MAX_DOCS_PER_ACTIVITY})`}
                                  <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                      handleProofUpload(en.id, e.target.files?.[0]);
                                      e.target.value = '';
                                    }}
                                  />
                                </label>
                              )
                            )}
                          </div>
                        </div>
                      ))}

                      {/* Add-activity form */}
                      <div
                        style={{
                          background: 'var(--warm)',
                          borderRadius: 10,
                          padding: 16,
                          marginBottom: 20,
                        }}
                      >
                        <div style={{ fontWeight: 600, marginBottom: 12 }}>
                          {entries.length === 0 ? 'Add an activity' : 'Add another activity'}
                        </div>
                        <div className="row2">
                          <div className="field">
                            <label>Activity</label>
                            <select
                              className={invalidClass('entryActivity')}
                              value={formActivity}
                              onChange={(e) => {
                                setFormActivity(e.target.value);
                                clearInvalid('entryActivity');
                              }}
                            >
                              {activities.length === 0 && <option value="">Loading…</option>}
                              {activities.map((a) => (
                                <option key={a.id} value={a.name}>
                                  {a.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="field">
                            <label>Highest level reached</label>
                            <select
                              value={formTier}
                              onChange={(e) => setFormTier(e.target.value)}
                            >
                              <option value="state">State ranking</option>
                              <option value="top300">National top-300</option>
                              <option value="championship">
                                National/international championship
                              </option>
                            </select>
                          </div>
                        </div>
                        <div className="row2">
                          <div className="field">
                            <label>Headline achievement</label>
                            <input
                              className={invalidClass('entryHeadline')}
                              placeholder="e.g. Einstein finalist"
                              value={formHeadline}
                              onChange={(e) => {
                                setFormHeadline(e.target.value);
                                clearInvalid('entryHeadline');
                              }}
                            />
                          </div>
                          <div className="field">
                            <label>Year</label>
                            <input
                              placeholder="e.g. 2024"
                              value={formYear}
                              onChange={(e) => setFormYear(e.target.value)}
                            />
                          </div>
                        </div>
                        {formIsRobotics && (
                          <div className="field">
                            <label>Team number</label>
                            <input
                              placeholder="e.g. 1678"
                              value={formTeam}
                              onChange={(e) => setFormTeam(e.target.value)}
                              style={{ maxWidth: 200 }}
                            />
                          </div>
                        )}
                        <div className="field">
                          <label>
                            Proof documents{' '}
                            <span className="meta">
                              (up to {MAX_DOCS_PER_ACTIVITY} — certificate, results screenshot,
                              roster)
                            </span>
                          </label>
                          {formDocs.map((d, di) => (
                            <div
                              key={`${d.url}-${di}`}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 6,
                              }}
                            >
                              <span className="meta">📄 {d.fileName}</span>
                              <button
                                className="btn btn-ghost btn-sm"
                                onClick={() =>
                                  setFormDocs((prev) => prev.filter((_, i) => i !== di))
                                }
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                          {uploadingKey === 'form' ? (
                            <span className="meta">Uploading…</span>
                          ) : (
                            formDocs.length < MAX_DOCS_PER_ACTIVITY && (
                              <label
                                className="btn btn-secondary btn-sm"
                                style={{ cursor: 'pointer' }}
                              >
                                {formDocs.length === 0
                                  ? 'Upload proof'
                                  : `Add another proof (${formDocs.length}/${MAX_DOCS_PER_ACTIVITY})`}
                                <input
                                  type="file"
                                  accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                                  style={{ display: 'none' }}
                                  onChange={(e) => {
                                    handleFormProofUpload(e.target.files?.[0]);
                                    e.target.value = '';
                                  }}
                                />
                              </label>
                            )
                          )}
                        </div>
                        <button className="btn btn-secondary" onClick={addEntry}>
                          Add activity
                        </button>
                      </div>

                      <button className="btn btn-primary" onClick={() => goStep(3)}>
                        Continue
                      </button>
                    </div>
                  )}

                  {/* 3 — Application grid */}
                  {step === 3 && (
                    <div className="card">
                      <h3 style={{ marginBottom: 4 }}>
                        {applicantType === 'parent' ? "Your kids' outcomes" : 'Your full application grid'}
                      </h3>
                      <p className="meta" style={{ marginBottom: 16 }}>
                        {applicantType === 'parent'
                          ? 'Where your kids applied and what happened. At least one admitted school is required.'
                          : "Required for listing: every school you applied to and what happened. Rejections included — that's the point."}
                      </p>
                      {grid.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                          {grid.map((g, i) => (
                            <button
                              key={`${g.university}-${i}`}
                              className={gridChipClass(g.result)}
                              onClick={() => removeGridEntry(i)}
                              title="Remove"
                              style={{ cursor: 'pointer', border: 'none', font: 'inherit' }}
                            >
                              {g.university}
                              {g.result !== 'Admitted' ? ` — ${g.result.toLowerCase()}` : ''}
                              {g.childName ? ` (${g.childName})` : ''} ✕
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="row3">
                        <div className="field">
                          <label>Add school</label>
                          <input
                            className={invalidClass('gridSchool')}
                            placeholder="School name"
                            value={gridSchool}
                            onChange={(e) => {
                              setGridSchool(e.target.value);
                              clearInvalid('gridSchool');
                            }}
                            onKeyDown={(e) => e.key === 'Enter' && addGridEntry()}
                          />
                        </div>
                        <div className="field">
                          <label>Result</label>
                          <select
                            value={gridResult}
                            onChange={(e) => setGridResult(e.target.value as GridResult)}
                          >
                            <option>Admitted</option>
                            <option>Rejected</option>
                            <option>Waitlisted</option>
                          </select>
                        </div>
                        <div className="field">
                          <label>&nbsp;</label>
                          <button className="btn btn-secondary btn-block" onClick={addGridEntry}>
                            Add
                          </button>
                        </div>
                      </div>
                      {applicantType === 'parent' && (
                        <div className="field">
                          <label>Child's first name (shown on outcomes)</label>
                          <input
                            placeholder="e.g. Marcus"
                            value={gridChild}
                            onChange={(e) => setGridChild(e.target.value)}
                          />
                        </div>
                      )}
                      <div className="note" style={{ marginBottom: 16 }}>
                        Your grid also joins the anonymized dataset behind the free annual report.
                        Names are never published.
                      </div>
                      <button className="btn btn-primary" onClick={() => goStep(4)}>
                        Continue
                      </button>
                    </div>
                  )}

                  {/* 4 — Sessions & rate (submit) */}
                  {step === 4 && (
                    <div className="card">
                      <h3 style={{ marginBottom: 16 }}>Sessions &amp; rate</h3>
                      <div className="row2">
                        <div className="field">
                          <label>Hourly rate</label>
                          <div style={{ fontSize: 24, fontWeight: 600 }}>${computedRate}/hour</div>
                          <p className="help">
                            Set by your highest verified tier: state $85 · national top-300 $110 ·
                            championship $150. You keep 80% of every session, paid out weekly.
                          </p>
                        </div>
                        <div className="field">
                          <label>Weekly availability</label>
                          <select>
                            <option>2–4 hours</option>
                            <option>1–2 hours</option>
                            <option>4+ hours</option>
                          </select>
                        </div>
                      </div>
                      <div className="field">
                        <label>
                          Session template{' '}
                          <span className="meta">(required — families see it before they book)</span>
                        </label>
                        <textarea
                          className={invalidClass('template')}
                          rows={4}
                          value={template}
                          onChange={(e) => {
                            setTemplate(e.target.value);
                            clearInvalid('template');
                          }}
                        ></textarea>
                      </div>
                      <div className="note" style={{ marginBottom: 16 }}>
                        After you submit, our team reviews your proof documents within 24–48
                        hours. Track your application and each credential's status on your
                        dashboard. Ground rule: mentors never promise outcomes or quote
                        "chances."
                      </div>
                      <button
                        className="btn btn-primary"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Submitting…' : 'Submit application'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default BecomeMentor;
